import { useMemo, useState } from 'react';
import { CalendarCheck, FileUp } from 'lucide-react';
import { formatDay } from '@nu/shared';
import { api } from '../../api/client.js';
import Modal from '../../components/Modal.jsx';
import { problem } from './util.js';

// Bookings > Import schedule: Network Plus's schedule of confirmed install
// jobs. Choosing the file checks it straight away and lists the jobs as the
// sheet has them: by gang, each block (the jobs under one heading row) apart,
// so "Lincoln's first 2, then his 3" is what you see. Tick jobs (or a whole
// block or gang), pick a date, Set date; then Import. A date already in the
// sheet wins. A job sent again updates its booking (matched on Network Plus's
// job number); one that's on the way, started or finished is left alone.
const RESULT = { new: 'New', updated: 'Updated', unchanged: 'No change', 'left alone': 'Already started: left alone', 'no date': 'Needs a date' };

export default function ScheduleImport({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [check, setCheck] = useState(null);
  const [dates, setDates] = useState({}); // order -> date picked here
  const [picked, setPicked] = useState(() => new Set());
  const [day, setDay] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const send = async (f, dryRun, jobDates) => {
    setBusy(true);
    setError(null);
    try {
      const q = new URLSearchParams({ fileName: f.name, jobDates: JSON.stringify(jobDates), ...(dryRun ? { dryRun: '1' } : {}) });
      const r = await api(`/bookings/schedule-imports?${q}`, { method: 'POST', raw: f });
      if (dryRun) setCheck(r);
      else onDone(`${r.added} booking${r.added === 1 ? '' : 's'} added, ${r.updated} updated${r.skipped ? `, ${r.skipped} not imported` : ''}.`);
      return r;
    } catch (e) {
      setError(problem(e));
      return null;
    } finally {
      setBusy(false);
    }
  };
  const pick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setFile(f);
    setCheck(null);
    setDates({});
    setPicked(new Set());
    send(f, true, {});
  };

  // Gang -> blocks -> jobs, in the sheet's order.
  const gangs = useMemo(() => {
    const out = [];
    for (const j of check?.jobs || []) {
      let g = out.find((x) => x.name === (j.gang || 'No gang name'));
      if (!g) { g = { name: j.gang || 'No gang name', blocks: [] }; out.push(g); }
      let b = g.blocks.find((x) => x.n === j.block);
      if (!b) { b = { n: j.block, jobs: [] }; g.blocks.push(b); }
      b.jobs.push(j);
    }
    return out;
  }, [check]);

  // The sheet's date, else one picked here, else the date it's already booked
  // for (a job sent again keeps its date unless you pick another).
  const dateOf = (j) => j.sheetDate || dates[j.order] || j.bookedDate || null;
  const toggle = (orders) => setPicked((p) => {
    const n = new Set(p);
    const allOn = orders.every((o) => n.has(o));
    orders.forEach((o) => (allOn ? n.delete(o) : n.add(o)));
    return n;
  });
  const setDate = () => {
    if (!day || !picked.size) return;
    setDates((d) => ({ ...d, ...Object.fromEntries([...picked].map((o) => [o, day])) }));
    setPicked(new Set());
  };
  const jobs = check?.jobs || [];
  const undated = jobs.filter((j) => !dateOf(j) && j.result !== 'left alone').length;
  const toImport = jobs.filter((j) => dateOf(j) && ['new', 'updated', 'no date'].includes(j.result)).length;
  const importNow = () => send(file, false, dates);

  return (
    <Modal
      open
      wide
      title="Import a Network Plus schedule"
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="button" className="btn primary" disabled={!check || busy || !toImport} onClick={importNow}>
            {busy && check ? 'Importing…' : `Import ${toImport} job${toImport === 1 ? '' : 's'}`}
          </button>
        </>
      )}
    >
      <div className="stack">
        <p className="small">The schedule of confirmed jobs as Network Plus sent it (.xlsx). Each job becomes a booking on its gang’s team (Gang Name matched to Match-Ups). A job that’s already booked is updated, not added again.</p>
        <label className="btn with-icon file-btn">
          <FileUp size={18} aria-hidden="true" /> {file ? 'Choose another schedule' : 'Choose schedule'}
          <input type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" hidden onChange={pick} />
        </label>
        {file && <p><strong>{file.name}</strong>{busy && !check ? ' · reading…' : ''}</p>}
        {error && <p className="notice-line notice-bad">{error}</p>}

        {check && (
          <>
            <div className="sched-dater">
              <span><strong>{picked.size}</strong> ticked</span>
              <input type="date" value={day} onChange={(e) => setDay(e.target.value)} aria-label="Date for the ticked jobs" />
              <button type="button" className="btn primary with-icon" disabled={!day || !picked.size} onClick={setDate}><CalendarCheck size={16} aria-hidden="true" /> Set date</button>
              <span className="small muted">{undated ? `${undated} job${undated === 1 ? '' : 's'} still need${undated === 1 ? 's' : ''} a date` : 'Every job has a date'}</span>
            </div>
            {check.noTeam.length > 0 && <p className="small warn-text">No team found for {check.noTeam.join(', ')} (not on Match-Ups, or not in a team that day): they’ll be booked with no team.</p>}
            {check.problems.map((p) => <p key={p} className="small warn-text">{p}</p>)}

            {gangs.map((g) => {
              const all = g.blocks.flatMap((b) => b.jobs.map((j) => j.order));
              return (
                <section key={g.name} className="sched-gang">
                  <label className="sched-gang-head">
                    <input type="checkbox" checked={all.every((o) => picked.has(o))} onChange={() => toggle(all)} />
                    <strong>{g.name}</strong>
                    <span className="small muted">{all.length} job{all.length === 1 ? '' : 's'}{g.blocks[0]?.jobs[0]?.team ? ` · ${g.blocks[0].jobs[0].team}` : ''}</span>
                  </label>
                  {g.blocks.map((b) => {
                    const orders = b.jobs.map((j) => j.order);
                    const blockDates = [...new Set(b.jobs.map(dateOf).filter(Boolean))];
                    return (
                      <div key={b.n} className="sched-block">
                        <label className="sched-block-head">
                          <input type="checkbox" checked={orders.every((o) => picked.has(o))} onChange={() => toggle(orders)} />
                          <span>Block {b.n}: {b.jobs.length} job{b.jobs.length === 1 ? '' : 's'}</span>
                          {blockDates.length === 1 && <span className="sched-date">{formatDay(blockDates[0])}</span>}
                        </label>
                        {b.jobs.map((j) => (
                          <label key={j.order} className={`sched-job${picked.has(j.order) ? ' on' : ''}`}>
                            <input type="checkbox" checked={picked.has(j.order)} onChange={() => toggle([j.order])} disabled={j.result === 'left alone'} />
                            <span className="sched-what"><strong>{j.address}</strong><span className="small muted">{j.postcode} · {j.order}</span></span>
                            <span className={`sched-res r-${j.result.replace(' ', '-')}`}>{dateOf(j) ? formatDay(dateOf(j)) : 'No date'}<span className="small">{j.result === 'no date' ? 'New' : RESULT[j.result]}{j.sheetDate ? ' · date from sheet' : !dates[j.order] && j.bookedDate ? ' · already booked' : ''}</span></span>
                          </label>
                        ))}
                      </div>
                    );
                  })}
                </section>
              );
            })}
          </>
        )}
      </div>
    </Modal>
  );
}
